import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';
import { CircularProgress, Box, LinearProgress, Typography } from '@mui/material';
import { Timer } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useRouter } from './use-router';
import { RootState } from '../store/store';
import { isLoginFunc } from '../store/slices/login';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { useRefreshtokenMutation, useLogoutMutation } from '../pages/login/services/authenticationApi';
import { clearPermissionCache } from '../hooks/usePermission';
import { globalVal } from '../utils/global-values';

const REFRESH_BLOCK_KEY = 'refreshBlockUntil';
const REFRESH_BLOCK_DURATION = 10000; // 10 saniye
const LOGOUT_REQUEST_BUFFER_SECONDS = 10;
const MODAL_COUNTDOWN_SECONDS = 60;
const MODAL_OPEN_BEFORE_EXP_SECONDS = MODAL_COUNTDOWN_SECONDS + LOGOUT_REQUEST_BUFFER_SECONDS; // 70 sn
const refreshChannel = new BroadcastChannel('auth-refresh');

let timerId: ReturnType<typeof setInterval> | null = null;

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { refreshExpired } = useSelector((state: RootState) => state.refreshExpired);
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const sessionToken = localStorage.getItem('accessToken');
  const location = useLocation();
  const router = useRouter();
  const dispatch = useDispatch();

  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [timeLeft, setTimeLeft] = useState(60000);
  const [progress, setProgress] = useState(100);
  const [openExitModal, setOpenExitModal] = useState(false);

  const [refreshTokenMutation, { isLoading: isLoadingRefresh }] = useRefreshtokenMutation();
  const [logout] = useLogoutMutation();
  const userActivityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const isLoggingOutRef = useRef(false);
  const continueFncRef = useRef<() => Promise<void>>(async () => {});
  const timerControlRef = useRef<() => void>(() => {});

  const mealCardRoutes = ['/cards/bulk-meal-card', '/cards/meal-allowance'];
  const isAccessingMealCardPage = mealCardRoutes.includes(location.pathname);
  const shouldBlockMealCardAccess =
    isAccessingMealCardPage &&
    (currentUserState?.isUsedMealCard === null || currentUserState?.isUsedMealCard === false);

  const logOutFnc = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    logout();
    clearPermissionCache();
    globalVal.isLoggedOut = true;
    dispatch(isLoginFunc(false));
    localStorage.clear();
    router.push('/login');
  }, [logout, dispatch, router]);

  function isRefreshBlocked() {
    const blockUntil = localStorage.getItem(REFRESH_BLOCK_KEY);
    if (!blockUntil) return false;
    return Date.now() < Number(blockUntil);
  }

  const timerControl = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const tokenExpirationTime = tokenPayload.exp;
        const currentTime = Math.floor(Date.now() / 1000);
        let remaining = tokenExpirationTime - currentTime;
        const adjustedRemaining = Math.max(remaining - LOGOUT_REQUEST_BUFFER_SECONDS, 0);
        setTimeLeft(adjustedRemaining);
        if (timerId) clearInterval(timerId);
        if (!isRefreshingRef.current && remaining <= LOGOUT_REQUEST_BUFFER_SECONDS) {
          logOutFnc();
          return;
        }
        timerId = setInterval(() => {
          setTimeLeft((prevTime) => {
            if (isRefreshingRef.current) return prevTime;
            if (prevTime <= 0) {
              logOutFnc();
              if (timerId) clearInterval(timerId);
              return 0;
            }
            const sessionToken = localStorage.getItem('accessToken');
            if (sessionToken === null) {
              if (timerId) clearInterval(timerId);
              return 0;
            }
            try {
              const tokenPayload = JSON.parse(atob(sessionToken.split('.')[1]));
              const currentTime = Math.floor(Date.now() / 1000);
              const tokenExpirationTime = tokenPayload.exp;
              const remaining = tokenExpirationTime - currentTime;
              const userActive = localStorage.getItem('isUserActive') === 'true';
              const adjustedRemaining = Math.max(remaining - LOGOUT_REQUEST_BUFFER_SECONDS, 0);
              if (!isRefreshingRef.current && remaining <= LOGOUT_REQUEST_BUFFER_SECONDS) {
                logOutFnc();
                if (timerId) clearInterval(timerId);
                return 0;
              }
              if (remaining <= MODAL_OPEN_BEFORE_EXP_SECONDS) {
                setProgress((adjustedRemaining / MODAL_COUNTDOWN_SECONDS) * 100);
                if (userActive) {
                  continueFncRef.current();
                  setOpenExitModal(false);
                } else {
                  setOpenExitModal(true);
                }
              } else {
                setOpenExitModal(false);
                setProgress(100);
              }
              return adjustedRemaining;
            } catch {
              return prevTime - 1;
            }
          });
        }, 1000);
      } catch {
        logOutFnc();
      }
    }
  }, [logOutFnc]);

  const continueFnc = useCallback(async () => {
    if (isRefreshBlocked()) return;
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      await refreshPromiseRef.current;
      return;
    }
    isRefreshingRef.current = true;
    localStorage.setItem(REFRESH_BLOCK_KEY, String(Date.now() + REFRESH_BLOCK_DURATION));
    refreshChannel.postMessage({ type: 'refreshing' });
    const promise = (async () => {
      try {
        if (timerId) clearInterval(timerId);
        setOpenExitModal(false);
        const refreshTokenFromStorage = localStorage.getItem('refreshToken');
        const response = await refreshTokenMutation({ refreshToken: refreshTokenFromStorage }).unwrap();
        const data = response as { success?: boolean; data?: { token: string; refreshToken: string } };
        if (data?.success && data?.data?.token) {
          localStorage.setItem('accessToken', data.data.token);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          localStorage.setItem('isUserActive', 'false');
          setOpenExitModal(false);
          timerControlRef.current();
          refreshChannel.postMessage({ type: 'token-updated', token: data.data.token });
        } else {
          logOutFnc();
        }
      } catch {
        logOutFnc();
      } finally {
        isRefreshingRef.current = false;
        refreshPromiseRef.current = null;
        refreshChannel.postMessage({ type: 'refresh-done' });
      }
    })();
    refreshPromiseRef.current = promise;
    await promise;
  }, [refreshTokenMutation, logOutFnc]);

  continueFncRef.current = continueFnc;
  timerControlRef.current = timerControl;

  useEffect(() => {
    refreshChannel.onmessage = (event: MessageEvent<{ type: string; token?: string }>) => {
      if (event.data.type === 'token-updated' && event.data.token) {
        localStorage.setItem('accessToken', event.data.token);
      }
      if (event.data.type === 'refreshing') {
        isRefreshingRef.current = true;
      }
      if (event.data.type === 'refresh-done') {
        isRefreshingRef.current = false;
      }
    };
    return () => {
      refreshChannel.onmessage = null;
    };
  }, []);

  const handleUserActivity = useCallback(() => {
    localStorage.setItem('isUserActive', 'true');
    if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
    userActivityTimeout.current = setTimeout(() => {
      localStorage.setItem('isUserActive', 'false');
    }, 120000);
  }, []);

  useEffect(() => {
    if (!openExitModal) {
      window.addEventListener('mousemove', handleUserActivity);
      window.addEventListener('keydown', handleUserActivity);
      window.addEventListener('mousedown', handleUserActivity);
      window.addEventListener('touchstart', handleUserActivity);
    }
    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
    };
  }, [openExitModal, handleUserActivity]);

  useEffect(() => {
    const checkTokenValidity = () => {
      if (!sessionToken) {
        setIsTokenValid(false);
        router.push('/login');
        return;
      }
      try {
        const tokenPayload = JSON.parse(atob(sessionToken.split('.')[1]));
        const currentTime = Date.now() / 1000;
        dispatch(isLoginFunc(true));
        setIsTokenValid(tokenPayload.exp > currentTime);
      } catch {
        setIsTokenValid(false);
      }
    };
    checkTokenValidity();
  }, [sessionToken, dispatch, router]);

  useEffect(() => {
    if (isTokenValid === false) {
      clearPermissionCache();
      globalVal.isLoggedOut = true;
      dispatch(isLoginFunc(false));
      localStorage.clear();
      router.push('/login');
    } else if (isTokenValid === true && location.pathname === '/login') {
      router.back();
    }
  }, [isTokenValid, location.pathname, router]);

  useEffect(() => {
    if (isRefreshingRef.current) return;
    if (timeLeft <= 0) {
      logOutFnc();
      if (timerId) clearInterval(timerId);
    }
  }, [timeLeft, logOutFnc]);

  useEffect(() => {
    timerControl();
    document.addEventListener('visibilitychange', timerControl);
    document.addEventListener('click', timerControl);
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', timerControl);
    }
    return () => {
      document.removeEventListener('visibilitychange', timerControl);
      document.removeEventListener('click', timerControl);
      if (timerId) clearInterval(timerId);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', timerControl);
      }
    };
  }, [refreshExpired, timerControl]);

  if (isTokenValid === null || !isTokenValid) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </div>
    );
  }

  if (shouldBlockMealCardAccess) {
    return <Navigate to="/dashboard/transactions" replace />;
  }

  return (
    <>
      <ConfirmationModal
        isLoading={isLoadingRefresh}
        isOpen={openExitModal}
        title="Oturum Süresi Dolmak Üzere"
        closeButtonText="Çıkış Yap"
        confirmButtonText="Devam Et"
        isLogin={true}
        hideCloseButton={true}
        onClose={logOutFnc}
        onCloseLogin={continueFnc}
        onConfirm={continueFnc}
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          titleColor: 'white',
          icon: <Timer sx={{ fontSize: 24, color: 'white', opacity: 0.95 }} />,
        }}
        content=""
        children={
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5 }}>
              Oturumunuz kısa süre içinde sonlanacak. Devam etmek için &quot;Devam Et&quot; butonuna tıklayın.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {timeLeft} saniye içinde otomatik çıkış yapılacak.
            </Typography>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: '10px',
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#0b6bcb',
                    borderRadius: '10px',
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: timeLeft <= 30 ? '#b91c1c' : 'grey.700',
                  fontWeight: 700,
                  textShadow: '0 0 8px rgba(255,255,255,0.9)',
                }}
              >
                {timeLeft} sn
              </Typography>
            </Box>
          </Box>
        }
      />
      <div
        style={{
          width: '100%',
          position: 'relative',
          backgroundColor: '#f4f5f7',
        }}
      >
        {children}
      </div>
    </>
  );
};

export default AuthGuard;
